/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	InsuranceConstants
} from '@insurance/constants/insurance-constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	ObjectHelper
} from '@shared/helpers/object.helper';
import {
	StringHelper
} from '@shared/helpers/string.helper';
import {
	IEntityInstance
} from '@shared/interfaces/entities/entity-instance.interface';

/**
 * A class containing static helper methods for insurance entities.
 *
 * @export
 * @class InsuranceHelper
 */
export class InsuranceHelper
{
	/**
	 * Gets or sets the set of commonly excluded transaction status values.
	 *
	 * @static
	 * @type {string[]}
	 * @memberof InsuranceHelper
	 */
	public static readonly transactionExcludedStatusValues: string[] =
		[
			InsuranceConstants.transactionStatusTypes.archived,
			InsuranceConstants.transactionStatusTypes.declined,
			InsuranceConstants.transactionStatusTypes.issued,
			InsuranceConstants.transactionStatusTypes.obsolete
		];

	/**
	 * Confirms that the status of the policy term transaction exists in the
	 * sent included status values and that this status value does not exist
	 * in the sent excluded status values.
	 *
	 * @static
	 * @param {string} status
	 * The policy term transaction status to be checked.
	 * @param {string[]} includedStatusValues
	 * If sent, this will be the set of possible status values that should
	 * return as a true conditional if it contains the sent status. This value
	 * defaults to an empty array.
	 * @param {string[]} excludedStatusValues
	 * If sent, this will be the set of possible status values that should
	 * return as a false conditional if it contains the sent status. This value
	 * will default to the set of archived, declined, issued, and obsolete
	 * status values.
	 * @returns {boolean}
	 * A value indicating whether the sent status value exists in the included
	 * status values and does not exist in the set of excluded status values.
	 * @memberof InsuranceHelper
	 */
	public static policyTermTransactionStatusCheck(
		status: string,
		includedStatusValues: string[] = [],
		excludedStatusValues: string[] = this.transactionExcludedStatusValues):
		boolean
	{
		return (includedStatusValues.length === 0
			|| includedStatusValues.includes(status))
			&& (excludedStatusValues.length === 0
				|| !excludedStatusValues.includes(status));
	}

	/**
	 * Gets the Ledger prior term transfer balance.
	 *
	 * @static
	 * @param {IEntityInstance[]} ledgerTransactions
	 * The LedgerTransaction entity instances.
	 * @return {number}
	 * The ledger prior term transfer balance.
	 * @memberof InsuranceHelper
	 */
	public static getPriorTermTransferBalance(
		ledgerTransactions: IEntityInstance[]): number
	{
		const transferBalance: number =
			ledgerTransactions.filter(
				ledgerTransaction =>
					ledgerTransaction.data.type ===
						InsuranceConstants.ledgerTransactionTypes.payment
						&& ledgerTransaction.data.category ===
							InsuranceConstants.ledgerTransactionCategories
								.transfer)
				.reduce(
					(sum, transfer) =>
						sum + transfer.data.amount,
					0);

		return transferBalance;
	}

	/**
	 * Gets the policy term written off balance.
	 *
	 * @static
	 * @param {IEntityInstance[]} ledgerTransactions
	 * The LedgerTransaction entity instances.
	 * @return {number}
	 * The policy term written off balance.
	 * @memberof InsuranceHelper
	 */
	public static getPolicyTermWrittenOffBalance(
		ledgerTransactions: IEntityInstance[]): number
	{
		const writtenOffBalance: number =
			ledgerTransactions.filter(
				ledgerTransaction =>
					ledgerTransaction.data.adjustmentType ===
						InsuranceConstants.ledgerTransactionAdjustmentTypes
							.writeOff)
				.reduce(
					(sum, transfer) =>
						sum + transfer.data.amount,
					0);

		return writtenOffBalance;
	}

	/**
	 * Gets the policy term fee balance from operational fees. This will
	 * not include the accounting written fee amount.
	 *
	 * @static
	 * @param {IEntityInstance} policyTerm
	 * The policy term associated to the ledger transaction set.
	 * @param {IEntityInstance[]} ledgerTransactions
	 * The LedgerTransaction entity instances.
	 * @return {number}
	 * The policy term operational fee balance.
	 * @memberof InsuranceHelper
	 */
	public static getPolicyTermOperationalFeeBalance(
		policyTerm: IEntityInstance,
		ledgerTransactions: IEntityInstance[]): number
	{
		const totalFeeBalance: number =
			ledgerTransactions.filter(
				ledgerTransaction =>
					ledgerTransaction.data.type ===
						InsuranceConstants.ledgerTransactionTypes.fee)
				.reduce(
					(sum, transfer) =>
						sum + transfer.data.amount,
					0);

		return totalFeeBalance - (policyTerm.data.accounting?.writtenFees ?? 0);
	}

	/**
	 * Gets the Ledger payments balance.
	 *
	 * @static
	 * @param {IEntityInstance[]} ledgerTransactions
	 * The LedgerTransaction entity instances.
	 * @return {number}
	 * The ledger payments balance.
	 * @memberof InsuranceHelper
	 */
	public static getPaymentsBalance(
		ledgerTransactions: IEntityInstance[]): number
	{
		const transferBalance: number =
			ledgerTransactions.filter(
				ledgerTransaction =>
					ledgerTransaction.data.type ===
						InsuranceConstants.ledgerTransactionTypes.payment
						&& ledgerTransaction.data.category !==
							InsuranceConstants.ledgerTransactionCategories
								.transfer)
				.reduce(
					(sum, transfer) =>
						sum + transfer.data.amount,
					0);

		return transferBalance;
	}

	/**
	 * Updates payment plan based values of the policy term object.
	 *
	 * @static
	 * @param {IEntityInstance} policyTerm
	 * The policy term entity to be updated.
	 * @param {string} paymentPlanId
	 * The payment plan id to set.
	 * @param {string} paymentPlanName
	 * The payment plan name to set.
	 * @param {string} paymentPlanVersion
	 * The payment plan version to set.
	 * @returns {IEntityInstance}
	 * A mapped policy term holding updated payment plan information based
	 * on the sent parameters.
	 * @memberof InsuranceHelper
	 */
	public static modifyPolicyTermPaymentPlan(
		policyTerm: IEntityInstance,
		paymentPlanId: string,
		paymentPlanName: string,
		paymentPlanVersion: string): IEntityInstance
	{
		policyTerm.data.preferences.paymentPlan =
			paymentPlanName;

		if (AnyHelper.isNull(
			policyTerm.data.accounting.paymentSchedule.paymentPlan))
		{
			policyTerm.data.accounting.paymentSchedule.paymentPlan =
				<any>
				{
					id: paymentPlanId,
					version: paymentPlanVersion
				};
		}
		else
		{
			policyTerm.data.accounting.paymentSchedule.paymentPlan.id =
				paymentPlanId;
			policyTerm.data.accounting.paymentSchedule.paymentPlan.version =
				paymentPlanVersion;
		}

		return policyTerm;
	}

	/**
	 * Given a policy term and future issued transaction, this will calculate
	 * the expected policy term accounting object written values. Note: This
	 * will not calculate or alter earned and should only be used for
	 * forecasting the term accounting.
	 *
	 * @static
	 * @param {IEntityInstance} policyTerm
	 * The policy term that will have it's accounting object updated.
	 * @param {IEntityInstance} policyTermTransaction
	 * The future issued or pending policy term transaction that will
	 * be used to update the policy term accounting object.
	 * @returns {Promise<IEntityInstance>}
	 * An updated policy term accounting object mapped from the future
	 * issued or pending sent policy term transaction.
	 * @memberof InsuranceHelper
	 */
	public static mapFutureTransactionToTermAccounting(
		policyTerm: IEntityInstance,
		policyTermTransaction: IEntityInstance): any
	{
		const policyTermAccounting =
			policyTerm.data.accounting;
		const policyTermTransactionAccounting =
			policyTermTransaction.data.accounting;

		policyTermAccounting.writtenPremium +=
			policyTermTransactionAccounting.directWrittenPremium;
		policyTermAccounting.writtenFees +=
			policyTermTransactionAccounting.directWrittenFees;
		policyTermAccounting.writtenTaxes +=
			policyTermTransactionAccounting.directWrittenTaxes;
		policyTermAccounting.writtenTotal +=
			policyTermTransactionAccounting.directWrittenTotal;

		policyTermAccounting.annualStatementLines =
			this.mapFutureAccountingSplitOuts(
				policyTermAccounting.annualStatementLines,
				policyTermTransactionAccounting.annualStatementLines);
		policyTermAccounting.fees =
			this.mapFutureAccountingSplitOuts(
				policyTermAccounting.fees,
				policyTermTransactionAccounting.fees);
		policyTermAccounting.taxes =
			this.mapFutureAccountingSplitOuts(
				policyTermAccounting.taxes,
				policyTermTransactionAccounting.taxes);

		return policyTermAccounting;
	}

	/**
	 * Gets the entity display name.
	 *
	 * @param {IEntityInstance} entityInstance
	 * The entity instance.
	 * @returns {string}
	 * The display name.
	 * @memberof InsuranceHelper
	 */
	public static getEntityDisplayName(
		entityInstance: IEntityInstance): string
	{
		return entityInstance.entityType
			=== InsuranceConstants.insuranceEntityTypes.user
			? StringHelper.toNameString(
				entityInstance.data.firstName,
				entityInstance.data.lastName)
			: StringHelper.fromNameObject(entityInstance.data.name);
	}

	/**
	 * Loads the reference options for the payment reference field.
	 *
	 * @static
	 * @param {IEntityInstance} entityInstance
	 * The entity instance to load the payment reference options from.
	 * @param {any} paymentAllocationSettings
	 * The payment allocation settings containing the path to the payment
	 * reference items.
	 * @returns {any[]}
	 * The list of payment reference options.
	 * @memberof InsuranceHelper
	 */
	public static loadPaymentReferenceOptions(
		entityInstance: IEntityInstance,
		paymentAllocationSettings: any): any[]
	{
		if(AnyHelper.isNull(entityInstance)
			|| AnyHelper.isNull(
				paymentAllocationSettings?.policy))
		{
			return [];
		}

		const items: any[] =
			InsuranceHelper.getPaymentReferenceItems(
				entityInstance,
				paymentAllocationSettings.policy);

		return this.mapPaymentReferenceItems(
			items,
			paymentAllocationSettings
				.policy
				.itemDescription,
			paymentAllocationSettings
				.policy
				.defaultAmountPath,
			paymentAllocationSettings
				.policy
				.label);
	}

	/**
	 * Loads the reference options for the commission payment reference field.
	 *
	 * @static
	 * @param {IEntityInstance} entityInstance
	 * The entity instance to load the commission
	 * payment reference options from.
	 * @param {any} paymentAllocationSettings
	 * The payment allocation settings containing
	 * the path to the commission payment reference items.
	 * @param {string} commissionCalculationIdentifier
	 * The identifier for the commission calculation.
	 * @returns {any[]}
	 * The list of commission payment reference options.
	 * @memberof InsuranceHelper
	 */
	public static loadCommissionPaymentReferenceOptions(
		entityInstance: IEntityInstance,
		paymentAllocationSettings: any,
		commissionCalculationIdentifier: string): any[]
	{
		if(AnyHelper.isNull(entityInstance)
			|| AnyHelper.isNull(
				paymentAllocationSettings?.commission))
		{
			return [];
		}

		const items: any[] =
			InsuranceHelper.getPaymentReferenceItems(
				entityInstance,
				paymentAllocationSettings.commission);

		const defaultAmountPath: string =
			paymentAllocationSettings
				.commission
				.defaultAmountPaths
				?.find((item: any) =>
					item.calculationIdentifier
						=== commissionCalculationIdentifier)
				?.defaultAmountPath;

		return this.mapPaymentReferenceItems(
			items,
			paymentAllocationSettings
				.commission
				.itemDescription,
			defaultAmountPath,
			paymentAllocationSettings
				.commission
				.label);
	}

	/**
	 * Gets the payment reference items from the given entity.
	 *
	 * @static
	 * @param {IEntityInstance} entityInstance
	 * The entity instance to get the payment reference items from.
	 * @param {any} paymentAllocationSettings
	 * The payment allocation settings containing the path to the payment
	 * reference items.
	 * @return {any[]}
	 * The list of payment reference items.
	 * @memberof InsuranceHelper
	 */
	public static getPaymentReferenceItems(
		entityInstance: IEntityInstance,
		paymentAllocationSettings: any): any[] {
		if (AnyHelper.isNull(entityInstance)
			|| AnyHelper.isNullOrWhitespace(
				paymentAllocationSettings?.path))
		{
			return [];
		}

		const result: any[] =
			ObjectHelper
				.getPropertyByJsonPath(
					entityInstance,
					paymentAllocationSettings.path)
					|| [];

		return Array.isArray(result)
			? result
			: [result];
	}

	/**
	 * Maps the payment reference items to the payment reference options.
	 *
	 * @static
	 * @param {any[]} items
	 * The list of payment reference items to map.
	 * @param {string} itemDescriptionPath
	 * The path to the item description in the payment reference items.
	 * @param {string} amountPath
	 * The path to the amount in the payment reference items.
	 * @param {string} defaultLabel
	 * The default label to use if the item description is not found.
	 * @returns {any[]}
	 * The list of mapped payment reference options.
	 * @memberof InsuranceHelper
	 */
	private static mapPaymentReferenceItems(
		items: any[],
		itemDescriptionPath: string,
		amountPath: string,
		defaultLabel: string): any[]
	{
		return items.map(
			(item: any, index: number) => ({
				id: item.resourceIdentifier,
				description:
					ObjectHelper
						.getPropertyByJsonPath(
							item,
							itemDescriptionPath)
						|| `${defaultLabel} ${index + 1}`,
				defaultAmount:
					ObjectHelper
						.getPropertyByJsonPath(
							item,
							amountPath)
			}));
	}

	/**
	 * Given a policy term split out set, this will calculate the mapped
	 * future split out of the policy term based on the transaction
	 * split outs in the same group with the same name.
	 *
	 *
	 * @static
	 * @param {IEntityInstance} termSplitOuts
	 * The policy term split outs that will have their accounting data updated.
	 * @param {IEntityInstance} transactionSplitOuts
	 * The future issued or pending policy term transaction split out that will
	 * be used to update the policy term accounting object.
	 * @returns {Promise<IEntityInstance>}
	 * An updated policy term accounting split out set mapped from the future
	 * issued or pending sent policy term transaction.
	 * @memberof InsuranceHelper
	 */
	private static mapFutureAccountingSplitOuts(
		termSplitOuts: any[],
		transactionSplitOuts: any[]): any
	{
		for (const termSplitOut of termSplitOuts)
		{
			const transactionSplitOut: any =
				transactionSplitOuts.find(
					(splitOut: any) =>
						splitOut.name === termSplitOut.name);

			if (!AnyHelper.isNull(transactionSplitOut))
			{
				termSplitOut.written +=
					transactionSplitOut.directWritten;
			}
		}

		for (const transactionSplitOut of transactionSplitOuts)
		{
			const termSplitOut: any =
				termSplitOuts.find(
					(splitOut: any) =>
						splitOut.name === transactionSplitOut.name);

			if (AnyHelper.isNull(termSplitOut))
			{
				termSplitOuts.push(
					<any>
					{
						name: transactionSplitOut.name,
						written: transactionSplitOut.directWritten
					});
			}
		}

		return termSplitOuts;
	}
}